// Debug component to help diagnose lab import issues
import React, { useState } from 'react';
import { 
  Modal, 
  Button, 
  Card, 
  Typography, 
  Space, 
  Alert, 
  Descriptions,
  Tag,
  Divider,
  message
} from 'antd';
import { 
  BugOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  InfoCircleOutlined 
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const LabImportDebugger = ({ visible, onClose, patientId }) => {
  const [debugResults, setDebugResults] = useState(null);
  const [testing, setTesting] = useState(false);

  const runDiagnostics = async () => {
    setTesting(true);
    const results = {
      timestamp: new Date().toISOString(),
      tests: []
    };

    try {
      // Test 1: Check if patientId is provided
      results.tests.push({
        name: 'Patient ID Check',
        status: patientId ? 'pass' : 'fail',
        message: patientId ? `Patient ID: ${patientId}` : 'No patient ID provided',
        details: patientId || 'Missing patient ID parameter'
      });

      // Test 2: Check authentication
      const token = localStorage.getItem('token');
      results.tests.push({
        name: 'Authentication Token',
        status: token ? 'pass' : 'fail',
        message: token ? 'Token found in localStorage' : 'No authentication token found',
        details: token ? `Token length: ${token.length} characters` : 'User may not be logged in'
      });

      // Test 3: Check axios configuration
      const baseURL = axios.defaults.baseURL;
      results.tests.push({
        name: 'Axios Base URL',
        status: baseURL ? 'pass' : 'warning',
        message: baseURL ? `Base URL: ${baseURL}` : 'No base URL configured',
        details: baseURL || 'Using relative URLs'
      });

      // Test 4: Test backend connectivity
      try {
        const healthResponse = await axios.get('/', { timeout: 5000 });
        results.tests.push({
          name: 'Backend Connectivity',
          status: 'pass',
          message: 'Backend server is accessible',
          details: `Response status: ${healthResponse.status}`
        });
      } catch (connectError) {
        results.tests.push({
          name: 'Backend Connectivity',
          status: 'fail',
          message: 'Cannot reach backend server',
          details: connectError.message
        });
      }

      // Test 5: Test authentication endpoint
      if (token && patientId) {
        try {
          const authTestResponse = await axios.get(`/patients/${patientId}`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000
          });
          results.tests.push({
            name: 'Authentication Test',
            status: 'pass',
            message: 'Authentication is working',
            details: `Successfully accessed patient data`
          });
        } catch (authError) {
          results.tests.push({
            name: 'Authentication Test',
            status: 'fail',
            message: 'Authentication failed',
            details: authError.response?.data?.message || authError.message
          });
        }
      }

      // Test 6: Test lab import endpoint accessibility
      if (token && patientId) {
        try {
          // Just test if the endpoint exists (should return 400 for missing data, not 404)
          await axios.post(`/labs/import-reviewed-labs/${patientId}`, {}, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000
          });
        } catch (endpointError) {
          const status = endpointError.response?.status;
          if (status === 400) {
            results.tests.push({
              name: 'Lab Import Endpoint',
              status: 'pass',
              message: 'Lab import endpoint is accessible',
              details: 'Endpoint exists and responds (400 expected for empty request)'
            });
          } else if (status === 404) {
            results.tests.push({
              name: 'Lab Import Endpoint',
              status: 'fail',
              message: 'Lab import endpoint not found',
              details: 'Route may not be registered or server not running'
            });
          } else {
            results.tests.push({
              name: 'Lab Import Endpoint',
              status: 'warning',
              message: `Unexpected response: ${status}`,
              details: endpointError.response?.data?.message || endpointError.message
            });
          }
        }
      }

      setDebugResults(results);
      
    } catch (error) {
      console.error('Debug test failed:', error);
      message.error('Debug test failed: ' + error.message);
    } finally {
      setTesting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'fail': return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'warning': return <InfoCircleOutlined style={{ color: '#faad14' }} />;
      default: return <InfoCircleOutlined />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'success';
      case 'fail': return 'error';
      case 'warning': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Modal
      title={
        <Space>
          <BugOutlined />
          <Title level={4} style={{ margin: 0 }}>
            Lab Import Debugger
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button 
          key="test" 
          type="primary" 
          loading={testing}
          onClick={runDiagnostics}
        >
          Run Diagnostics
        </Button>
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Alert
          message="Lab Import Diagnostic Tool"
          description="This tool helps identify issues with the lab PDF import functionality. Click 'Run Diagnostics' to test various components."
          type="info"
          showIcon
        />

        {debugResults && (
          <Card title="Diagnostic Results" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Test Time">
                {new Date(debugResults.timestamp).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Total Tests">
                {debugResults.tests.length}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Space>
                  <Tag color="success">
                    {debugResults.tests.filter(t => t.status === 'pass').length} Passed
                  </Tag>
                  <Tag color="error">
                    {debugResults.tests.filter(t => t.status === 'fail').length} Failed
                  </Tag>
                  <Tag color="warning">
                    {debugResults.tests.filter(t => t.status === 'warning').length} Warnings
                  </Tag>
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Space direction="vertical" style={{ width: '100%' }}>
              {debugResults.tests.map((test, index) => (
                <Card key={index} size="small" style={{ marginBottom: 8 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      {getStatusIcon(test.status)}
                      <Text strong>{test.name}</Text>
                      <Tag color={getStatusColor(test.status)}>
                        {test.status.toUpperCase()}
                      </Tag>
                    </Space>
                    <Text>{test.message}</Text>
                    {test.details && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Details: {test.details}
                      </Text>
                    )}
                  </Space>
                </Card>
              ))}
            </Space>
          </Card>
        )}

        <Card title="Common Solutions" size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Paragraph>
              <Text strong>If authentication fails:</Text>
              <br />
              • Try logging out and logging back in
              <br />
              • Check if your session has expired
            </Paragraph>
            
            <Paragraph>
              <Text strong>If backend is not accessible:</Text>
              <br />
              • Make sure the backend server is running
              <br />
              • Check the server URL configuration
              <br />
              • Verify there are no firewall or network issues
            </Paragraph>
            
            <Paragraph>
              <Text strong>If endpoints are not found:</Text>
              <br />
              • Verify the backend routes are properly registered
              <br />
              • Check if the lab routes module is loaded
              <br />
              • Ensure the server is running the latest code
            </Paragraph>
          </Space>
        </Card>
      </Space>
    </Modal>
  );
};

export default LabImportDebugger;
